import { useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { format } from "date-fns";

import { FilterOptions } from "./Graphs/graph-helpers";
import { FilterToggle } from "./Graphs/FilterToggle";

import { Company } from "@otta/search/schema";
import { HorizontalChart, Spacing, Text, media } from "@otta/design";
import { palette, pxToRem } from "@otta/design-tokens";

const Container = styled.div<{ showBorder: boolean }>`
  ${({ showBorder }) =>
    showBorder &&
    css`
      padding: ${pxToRem(16)} 0;
      border-top: ${pxToRem(1)} solid #d5d5d5;
      margin-top: ${pxToRem(16)};
    `}
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${media({ to: "desktop" })`
    flex-direction: column;
    align-items: flex-start;
    gap: ${pxToRem(16)};
  `};
`;

export const PeopleBreakdown = ({
  company,
  showBorder = false,
}: {
  company: Company.Company;
  showBorder?: boolean;
}) => {
  const [currentFilter, setCurrentFilter] =
    useState<FilterOptions>("workingLocations");

  const peopleBreakdown = company.ebStatistics?.peopleBreakdown;

  const updatedAt =
    company.ebStatistics?.updatedAt &&
    format(new Date(company.ebStatistics?.updatedAt), "EEEE dd MMM yyyy");

  const filters = useMemo(() => {
    const filtersArray: FilterOptions[] = [];

    if (peopleBreakdown === undefined) {
      return [];
    }

    if (
      !!peopleBreakdown.workingLocations &&
      peopleBreakdown.workingLocations.length > 0
    ) {
      filtersArray.push("workingLocations");
    }

    if (!!peopleBreakdown.genders && peopleBreakdown.genders.length > 0) {
      filtersArray.push("genders");
    }

    if (
      !!peopleBreakdown.ethnicities &&
      peopleBreakdown.ethnicities.length > 0
    ) {
      filtersArray.push("ethnicities");
    }

    if (filtersArray.length > 0) {
      setCurrentFilter(filtersArray[0] as FilterOptions);
    }

    return filtersArray;
  }, [peopleBreakdown]);

  if (
    peopleBreakdown === undefined ||
    !peopleBreakdown[currentFilter] ||
    filters.length === 0
  ) {
    return null;
  }

  return (
    <Container showBorder={showBorder}>
      <Spacing>
        <TitleSection data-testid="people-breakdown">
          <Text bold size={1}>
            People breakdown
          </Text>

          <FilterToggle
            currentFilter={currentFilter}
            changeFilter={filter => setCurrentFilter(filter)}
            filters={filters}
            section="people-breakdown"
          />
        </TitleSection>
        <HorizontalChart data={peopleBreakdown[currentFilter] ?? []} />

        {updatedAt && (
          <Text size={-1} color={palette.grayscale.shade400}>
            Last updated on {updatedAt}.
          </Text>
        )}
      </Spacing>
    </Container>
  );
};
